import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import {
  STATUS_ICON_CHECK_O,
  STATUS_ICON_WARNING_O,
  STATUS_ICON_WARNING,
  STATUS_ICON_LOADING,
  STATUS_ICON_TIMES_O,
  STATUS_ICON_MAINTENANCE,
  STATE_ICON_UNASSIGNED,
  STATE_ICON_UNAVAILABLE
} from '@/common/utils/statuses';
import { ChunkServer_Status, DiskDetails, SystemdStatus, Storage_JournalType, DiskRole } from '@schema';

export const MAX_DIFFERENCE_IN_SINGLE_TIER = 1.05; // 5%

export const STORAGE_DISK_TARGET_STATUS_FAILED = 'failed';
export const STORAGE_DISK_TARGET_STATUS_SUCCESS = 'connected';
export const STORAGE_DISK_TARGET_STATUS_DISCONNECTED = 'disconnected';

export const STORAGE_DISK_TYPE_HDD = 'hdd';
export const STORAGE_DISK_TYPE_SSD = 'ssd';
export const STORAGE_DISK_TYPE_ISCSI = 'iscsi';
export const STORAGE_DISK_TYPE_SMR = 'smr';
export const STORAGE_DISK_TYPE_NVME = 'nvme';

export const STORAGE_DISK_TYPE_TEXT = computedLocale(() => ({
  [STORAGE_DISK_TYPE_HDD]: i18n.t('HDD'),
  [STORAGE_DISK_TYPE_SSD]: i18n.t('SSD'),
  [STORAGE_DISK_TYPE_ISCSI]: i18n.t('iSCSI'),
  [STORAGE_DISK_TYPE_SMR]: i18n.t('Zoned (SMR)'),
  [STORAGE_DISK_TYPE_NVME]: i18n.t('NVMe')
}));

export const STORAGE_DISK_TYPE_ICONS_WITH_INDICATOR = {
  [STORAGE_DISK_TYPE_HDD]: 'i-hdd-s-o--24',
  [STORAGE_DISK_TYPE_SSD]: 'i-ssd-s-o--24',
  [STORAGE_DISK_TYPE_ISCSI]: 'i-lun-s-o--24',
  [STORAGE_DISK_TYPE_SMR]: 'i-hdd-s-o--24',
  [STORAGE_DISK_TYPE_NVME]: 'i-nvme-s-o--24',
};

export const STORAGE_DISK_TYPE_ICONS = {
  [STORAGE_DISK_TYPE_HDD]: 'i-hdd-o--24',
  [STORAGE_DISK_TYPE_SSD]: 'i-ssd-o--24',
  [STORAGE_DISK_TYPE_ISCSI]: 'i-lun-o--24',
  [STORAGE_DISK_TYPE_SMR]: 'i-hdd-o--24',
  [STORAGE_DISK_TYPE_NVME]: 'i-nvme-o--24',
};

export type DiskRoleMap = Record<DiskRole, string>

export const STORAGE_DISK_ROLE_TEXT = computedLocale((): DiskRoleMap => ({
  [DiskRole.CS]: i18n.t('Storage'),
  [DiskRole.MDS]: i18n.t('Metadata'),
  [DiskRole.JOURNAL]: i18n.t('Cache'),
  [DiskRole.MDS_JOURNAL]: i18n.t('Metadata+Cache'),
  [DiskRole.MDS_SYSTEM]: i18n.t('Metadata'),
  [DiskRole.CS_SYSTEM]: i18n.t('Storage'),
  [DiskRole.SYSTEM]: i18n.t('figure dash'),
  [DiskRole.SWAP]: i18n.t('figure dash'),
  [DiskRole.UNASSIGNED]: i18n.t('figure dash')
}));

export const STORAGE_DISK_ROLE_SELECT_TEXT = computedLocale((): DiskRoleMap => ({
  [DiskRole.CS]: i18n.t('Storage'),
  [DiskRole.CS_SYSTEM]: i18n.t('Storage'),
  [DiskRole.MDS]: i18n.t('Metadata'),
  [DiskRole.MDS_SYSTEM]: i18n.t('Metadata'),
  [DiskRole.JOURNAL]: i18n.t('Cache'),
  [DiskRole.MDS_JOURNAL]: i18n.t('Metadata+Cache'),
  [DiskRole.SYSTEM]: i18n.t('Not assigned'),
  [DiskRole.UNASSIGNED]: i18n.t('Not assigned'),
  [DiskRole.SWAP]: i18n.t('Not assigned')
}));

export type HardStatusMap = Record<DiskDetails.disk_status, string>

export const STORAGE_DISK_HARD_STATUS_TEXT = computedLocale((): HardStatusMap => ({
  [DiskDetails.disk_status.FAILED]: i18n.t('Failed'),
  [DiskDetails.disk_status.UNAVAIL]: i18n.t('Unavailable'),
  [DiskDetails.disk_status.OK]: i18n.t('Healthy'),
  [DiskDetails.disk_status.RELEASING]: i18n.t('Releasing')
}));

export type ServiceStatusMap = Record<ChunkServer_Status | DiskDetails.status, string>

export const STORAGE_DISK_SERVICE_STATUS_TEXT = computedLocale((): ServiceStatusMap => ({
  [ChunkServer_Status.ACTIVE]: i18n.t('Active'),
  [ChunkServer_Status.DROPPED]: i18n.t('Dropped'),
  [ChunkServer_Status.ENTERING_MAINTENANCE]: i18n.t('Entering maintenance'),
  [ChunkServer_Status.FAILED]: i18n.t('Failed'),
  [ChunkServer_Status.FAILED_REL]: i18n.t('Release failed'),
  [ChunkServer_Status.ILL]: i18n.t('Unresponsive'),
  [ChunkServer_Status.INACTIVE]: i18n.t('Inactive'),
  [ChunkServer_Status.MAINTENANCE]: i18n.t('Maintenance'),
  [ChunkServer_Status.NO_SPACE]: i18n.t('Out of space'),
  [ChunkServer_Status.OFFLINE]: i18n.t('Offline'),
  [ChunkServer_Status.RELEASING]: i18n.t('Releasing'),
  [ChunkServer_Status.UNAVAILABLE]: i18n.t('Unavailable'),
  [ChunkServer_Status.UNKNOWN]: i18n.t('Unknown'),
  [ChunkServer_Status.UNRECOGNIZED]: i18n.t('Unrecognized'),
  [DiskDetails.status.AVAIL]: i18n.t('Available'),
  [DiskDetails.status.STALE]: i18n.t('Syncing'),
  [DiskDetails.status.UNAVAIL]: i18n.t('Unavailable'),
}));

export const STORAGE_DISK_SERVICE_STATUS_COLOR_MAP: Record<ChunkServer_Status, string> = {
  [ChunkServer_Status.ACTIVE]: '#9BC225',
  [ChunkServer_Status.DROPPED]: '#C62020',
  [ChunkServer_Status.ENTERING_MAINTENANCE]: '#FFC8AA',
  [ChunkServer_Status.FAILED]: '#FFC5C5',
  [ChunkServer_Status.FAILED_REL]: '#EA3939',
  [ChunkServer_Status.ILL]: '#FF810D',
  [ChunkServer_Status.INACTIVE]: '#B5381C',
  [ChunkServer_Status.MAINTENANCE]: '#FFC107',
  [ChunkServer_Status.NO_SPACE]: '#FFE8A3',
  [ChunkServer_Status.OFFLINE]: '#E9EAEC',
  [ChunkServer_Status.RELEASING]: '#1F56A2',
  [ChunkServer_Status.UNAVAILABLE]: '#949AA3',
  [ChunkServer_Status.UNKNOWN]: '#949AA3',
  [ChunkServer_Status.UNRECOGNIZED]: '#CADEF9'
};

export const STORAGE_DISK_SERVICE_STATUS_DASHBOARD_ORDER: Array<ChunkServer_Status> = [
  ChunkServer_Status.FAILED,
  ChunkServer_Status.FAILED_REL,
  ChunkServer_Status.DROPPED,
  ChunkServer_Status.UNKNOWN,
  ChunkServer_Status.OFFLINE,
  ChunkServer_Status.UNAVAILABLE,
  ChunkServer_Status.INACTIVE,
  ChunkServer_Status.UNRECOGNIZED,
  ChunkServer_Status.NO_SPACE,
  ChunkServer_Status.ILL,
  ChunkServer_Status.MAINTENANCE,
  ChunkServer_Status.ENTERING_MAINTENANCE,
  ChunkServer_Status.RELEASING,
  ChunkServer_Status.ACTIVE,
];

export const STORAGE_DISK_SERVICE_STATUS_DASHBOARD_TEXT = computedLocale<Record<ChunkServer_Status, string>>(() => ({
  [ChunkServer_Status.ACTIVE]: i18n.t('CS active'),
  [ChunkServer_Status.DROPPED]: i18n.t('CS dropped'),
  [ChunkServer_Status.ENTERING_MAINTENANCE]: i18n.t('CS entering maintenance'),
  [ChunkServer_Status.FAILED]: i18n.t('CS failed'),
  [ChunkServer_Status.FAILED_REL]: i18n.t('CS release failed'),
  [ChunkServer_Status.ILL]: i18n.t('CS unresponsive'),
  [ChunkServer_Status.INACTIVE]: i18n.t('CS inactive'),
  [ChunkServer_Status.MAINTENANCE]: i18n.t('CS maintenance'),
  [ChunkServer_Status.NO_SPACE]: i18n.t('CS out of space'),
  [ChunkServer_Status.OFFLINE]: i18n.t('CS offline'),
  [ChunkServer_Status.RELEASING]: i18n.t('CS releasing'),
  [ChunkServer_Status.UNAVAILABLE]: i18n.t('CS unavailable'),
  [ChunkServer_Status.UNKNOWN]: i18n.t('CS unknown'),
  [ChunkServer_Status.UNRECOGNIZED]: i18n.t('CS unrecognized')
}));

export const STORAGE_DISK_STATUS_ICON: ServiceStatusMap = {
  [ChunkServer_Status.ACTIVE]: STATUS_ICON_CHECK_O,
  [ChunkServer_Status.ILL]: STATUS_ICON_WARNING,
  [ChunkServer_Status.INACTIVE]: STATUS_ICON_WARNING_O,
  [ChunkServer_Status.OFFLINE]: STATE_ICON_UNAVAILABLE,
  [ChunkServer_Status.NO_SPACE]: STATUS_ICON_WARNING,
  [ChunkServer_Status.RELEASING]: STATUS_ICON_LOADING,
  [ChunkServer_Status.FAILED]: STATUS_ICON_TIMES_O,
  [ChunkServer_Status.FAILED_REL]: STATUS_ICON_TIMES_O,
  [ChunkServer_Status.MAINTENANCE]: STATUS_ICON_MAINTENANCE,
  [ChunkServer_Status.UNKNOWN]: STATE_ICON_UNASSIGNED,
  [ChunkServer_Status.ENTERING_MAINTENANCE]: STATUS_ICON_LOADING,
  [ChunkServer_Status.DROPPED]: STATUS_ICON_TIMES_O,
  [ChunkServer_Status.UNAVAILABLE]: STATE_ICON_UNAVAILABLE,
  [ChunkServer_Status.UNRECOGNIZED]: STATE_ICON_UNAVAILABLE,
  [DiskDetails.status.AVAIL]: STATUS_ICON_CHECK_O,
  [DiskDetails.status.STALE]: STATUS_ICON_LOADING,
  [DiskDetails.status.UNAVAIL]: STATE_ICON_UNAVAILABLE,
};

export type SmartStatusMap = Record<DiskDetails.smart_status, string>

export const STORAGE_DISK_SMART_STATUS_TEXT = computedLocale((): SmartStatusMap => ({
  [DiskDetails.smart_status.PASSED]: i18n.t('Passed'),
  [DiskDetails.smart_status.NOT_SUPPORTED]: i18n.t('Unsupported'),
  [DiskDetails.smart_status.FAILED]: i18n.t('Failed'),
}));

export type SystemdStatusMap = Record<SystemdStatus, string>

export const STORAGE_DISK_SYSTEMD_STATUS_TEXT = computedLocale((): SystemdStatusMap => ({
  [SystemdStatus.ACTIVATING]: i18n.t('Activating'),
  [SystemdStatus.ACTIVE]: i18n.t('Active'),
  [SystemdStatus.DEACTIVATING]: i18n.t('Deactivating'),
  [SystemdStatus.FAILED]: i18n.t('Failed'),
  [SystemdStatus.INACTIVE]: i18n.t('Inactive'),
  [SystemdStatus.RELOADING]: i18n.t('Reloading'),
  [SystemdStatus.UNKNOWN]: i18n.t('Unknown')
}));

export type JournalTypeStatusMap = Record<Storage_JournalType, string>
export const STORAGE_DISK_JOURNAL_TYPE_TEXT = computedLocale((): JournalTypeStatusMap => ({
  [Storage_JournalType.EXTERNAL_CACHE]: i18n.t('SSD caching and checksumming enabled'),
  [Storage_JournalType.INNER_CACHE]: i18n.t('Checksumming enabled'),
  [Storage_JournalType.NO_CACHE]: i18n.t('Disabled')
}));

export const STORAGE_DISK_ASSIGN_WARNING_TYPE = computedLocale(() => ({
  SystemAndNotDisk: {
    key: 'SystemAndNotDisk',
    text: i18n.t('To be able to assign roles to the disks, select the system disk and unassigned disks separately.')
  },
  SMRWithoutCache: {
    key: 'SMRWithoutCache',
    text: i18n.t('To be able to use an SMR disk, enable SSD caching for the tier that will be assigned to this SMR disk. SMR disks are only supported with enabled SSD caching.')
  },
  DiskLessWhen1Gib: {
    key: 'DiskLessWhen1Gib',
    text: i18n.t('To be able to assign a role to the disk, its size must be greater than 1 GiB.')
  },
  ISCSIDiskLessThen3Gib: {
    key: 'ISCSIDiskLessThen3Gib',
    text: i18n.t('To be able to assign a role to the iSCSI disk, its size must be greater than 2 GiB.')
  },
  SystemDiskLessWhen100Gib: {
    key: 'SystemDiskLessWhen100Gib',
    text: i18n.t('To be able to assign a role to the system disk, its size must be at least 100 GiB.')
  },
  SMRAndNot: {
    key: 'SMRAndNot',
    text: i18n.t('Cannot use SMR and standard disks in the same tier.')
  }
}));
